/* eslint-disable no-use-before-define */
import React, { useMemo } from 'react';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import referencesService, { Team } from '../../services/references';
import useReferenceList from './useReferenceList';
import Progress from './Progress';
import { useSnackbar } from 'notistack';

interface Props {
  value: string | null;
  label?: string;
  addTeam?: boolean;
  onChange: (team: Team) => void;
}

type TeamOptionType = Team & { inputValue?: string };

const filter = createFilterOptions<TeamOptionType>();

const nameSort = (a: Team, b: Team) => {
  if (a.name && b.name) {
    return a.name > b.name ? 1 : -1;
  }
  return 0;
};

const SelectSingleTeam: React.FunctionComponent<Props> = ({ value, label, onChange, addTeam }) => {
  const [open, toggleOpen] = React.useState(false);
  const { data, loading, error, append } = useReferenceList('teams', referencesService.getTeams);
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    setDialogValue({
      name: '',
    });
    toggleOpen(false);
  };

  const [dialogValue, setDialogValue] = React.useState({
    name: '',
  });

  const sortedData = useMemo(() => {
    return data?.slice().sort(nameSort) || [];
  }, [data]);

  const selected = useMemo(() => {
    return (sortedData.find((d) => d.id === value) as TeamOptionType) || null;
  }, [sortedData, value]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    referencesService
      .addTeam(dialogValue.name)
      .then((team) => {
        append(team);
        onChange(team);
        handleClose();
      })
      .catch(() => enqueueSnackbar('Failed to add team', { variant: 'error', autoHideDuration: 3000 }));
  };

  if (error) {
    enqueueSnackbar('Failed to load teams', { variant: 'error', autoHideDuration: 3000 });
  }

  if (loading) {
    return <Progress />;
  }

  return (
    <React.Fragment>
      <Autocomplete
        value={selected}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            // timeout to avoid instant validation of the dialog's form.
            if (addTeam) {
              setTimeout(() => {
                toggleOpen(true);
                setDialogValue({
                  name: newValue,
                });
              });
            }
          } else if (newValue && newValue.inputValue) {
            if (addTeam) {
              toggleOpen(true);
              setDialogValue({
                name: newValue.inputValue,
              });
            }
          } else if (newValue) {
            onChange(newValue);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (params.inputValue !== '' && addTeam) {
            filtered.push({
              id: '',
              inputValue: params.inputValue,
              name: `Add "${params.inputValue}"`,
            });
          }

          return filtered;
        }}
        id="team-autocomplete"
        options={sortedData as TeamOptionType[]}
        getOptionLabel={(option) => {
          // e.g value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return option.name;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        style={{ marginTop: 16, marginBottom: 8 }}
        renderOption={(option) => option.name}
        freeSolo={addTeam || false}
        renderInput={(params) => <TextField {...params} label={label || 'Team'} variant="standard" />}
      />
      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <form onSubmit={handleSubmit}>
          <DialogTitle id="form-dialog-title">Add a new team</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              value={dialogValue.name}
              onChange={(event) => setDialogValue({ ...dialogValue, name: event.target.value })}
              label="Team name"
              type="text"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary">
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
};

export default SelectSingleTeam;
